"use client";

import { useState, useRef } from "react";
import { Search, ChevronDown, ArrowDown, X, Users } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { cn } from "@/lib/utils";
import { toast } from "@/lib/toast";
import { postWithAuth } from "@/lib/api-helpers";
import type { WorkArea } from "@/types/workarea";
import type { Members } from "@/types/workspace";
import { MemberHoverAvatar } from "@/components/tailwind/ui/MemberHoverAvatar";
import { AnchoredPopover } from "@/components/tailwind/ui/anchored-popover";
import WorkAreaAddMembersModal from "../WorkAreaAddMembersModal";
import { removeGroupAccessFromWorkArea } from "@/services-frontend/workspace/groupServices";

interface MembersTabProps {
  workArea: WorkArea;
  workspaceMembers: Members[];
  workspaceOwnerId?: string;
  isOwner: boolean;
  onMembersUpdated?: () => void;
}

const AVATAR_COLORS = [
  "bg-red-500",
  "bg-orange-500",
  "bg-amber-500",
  "bg-yellow-500",
  "bg-lime-500",
  "bg-green-500",
  "bg-emerald-500",
  "bg-teal-500",
  "bg-cyan-500",
  "bg-sky-500",
  "bg-blue-500",
  "bg-indigo-500",
  "bg-violet-500",
  "bg-purple-500",
  "bg-fuchsia-500",
  "bg-pink-500",
  "bg-rose-500",
];

function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length] || "bg-zinc-500";
}

export default function MembersTab({
  workArea,
  workspaceMembers,
  workspaceOwnerId,
  isOwner,
  onMembersUpdated,
}: MembersTabProps) {
  const { user } = useAuth();
  const { workspaceGroups } = useWorkspaceContext();
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [sortBy, setSortBy] = useState<"name" | "role">("role");
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [removingGroupId, setRemovingGroupId] = useState<string | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const workAreaMembersWithDetails = (workArea.members || []).map((waMember) => {
    const fullMember = workspaceMembers.find(
      (m) => m.userEmail === waMember.userEmail || m.userId === String(waMember.userId),
    );
    const isWorkAreaOwner =
      String(waMember.userId) === String(workArea.ownerId) ||
      waMember.userId === workArea.ownerId;
    const isWorkspaceOwner =
      !!workspaceOwnerId &&
      (String(waMember.userId) === String(workspaceOwnerId) ||
        waMember.userId === workspaceOwnerId ||
        String(fullMember?.userId) === String(workspaceOwnerId));
    return {
      ...fullMember,
      ...waMember,
      // Prefer the live directory name/image over the stale stored work-area
      // member snapshot so profile edits show up here too.
      userName: fullMember?.userName || waMember.userName,
      image: fullMember?.image ?? (waMember as { image?: string }).image,
      isWorkAreaOwner,
      isWorkspaceOwner,
    };
  });

  const filteredAndSortedMembers = workAreaMembersWithDetails
    .filter((member) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        member.userName?.toLowerCase().includes(query) ||
        member.userEmail?.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      if (sortBy === "role") {
        if (a.isWorkAreaOwner && !b.isWorkAreaOwner) return -1;
        if (!a.isWorkAreaOwner && b.isWorkAreaOwner) return 1;
      }
      const nameA = a.userName || a.userEmail || "";
      const nameB = b.userName || b.userEmail || "";
      return nameA.localeCompare(nameB);
    });

  const handleAddMembersClick = () => setShowAddMembersModal(true);
  const handleMembersAdded = () => {
    setShowAddMembersModal(false);
    onMembersUpdated?.();
  };

  const getRoleDisplay = (isWorkAreaOwner: boolean) =>
    isWorkAreaOwner ? "Workarea owner" : "Workarea member";

  const getGroupPermissionDisplay = (permission?: "full" | "edit" | "view") => {
    if (permission === "full") return "Full access";
    if (permission === "view") return "View access";
    return "Edit access";
  };

  const handleRemoveMember = async (member: {
    userId?: string;
    userEmail?: string;
    userName?: string;
    isWorkAreaOwner: boolean;
  }) => {
    if (!isOwner) return;
    if (member.isWorkAreaOwner) {
      toast.error("Cannot remove the work area owner");
      return;
    }
    if (
      !confirm(
        `Are you sure you want to remove ${member.userName || member.userEmail} from this work area?`,
      )
    ) {
      return;
    }
    const memberKey = member.userId || member.userEmail || "";
    setRemovingMemberId(memberKey);
    setOpenDropdownId(null);
    try {
      const response = await postWithAuth("/api/workarea/remove-member", {
        workAreaId: workArea._id,
        memberId: member.userId || member.userEmail,
      });
      if (response && !(response as { isError?: boolean }).isError) {
        toast.success(
          `${member.userName || member.userEmail} has been removed from the work area`,
        );
        onMembersUpdated?.();
      } else {
        throw new Error("Failed to remove member");
      }
    } catch (error) {
      console.error("Error removing member:", error);
      toast.error("Failed to remove member");
    } finally {
      setRemovingMemberId(null);
    }
  };

  const handleRemoveGroup = async (group: { gid: string; displayName: string }) => {
    if (!isOwner) return;
    if (!confirm(`Are you sure you want to remove ${group.displayName} from this work area?`)) {
      return;
    }
    setRemovingGroupId(group.gid);
    setOpenDropdownId(null);
    try {
      await removeGroupAccessFromWorkArea({
        workAreaId: workArea._id,
        groupId: group.gid,
      });
      toast.success(`${group.displayName} has been removed from the work area`);
      onMembersUpdated?.();
    } catch (error) {
      console.error("Error removing group:", error);
      toast.error(error instanceof Error ? error.message : "Failed to remove group");
    } finally {
      setRemovingGroupId(null);
    }
  };

  const getMemberId = (member: { userId?: string; userEmail?: string }) =>
    member.userId || member.userEmail || "";

  // Group rows live in the same list as members so the access table reads as
  // one question ("who has access?") instead of two. Each entry is shaped to
  // match a member row: avatar, name + sub-line, role column.
  const groupAccessList = workArea.groupAccess || [];
  const filteredGroups = groupAccessList
    .map((g) => {
      const gid = String(g.groupId);
      const ws = (workspaceGroups || []).find(
        (wg) => String(wg.id || (wg as any)._id) === gid,
      );
      return {
        gid,
        displayName: ws?.name || g.groupName || "Unknown group",
        memberCount: ws?.members?.length ?? 0,
        permission: g.permission,
      };
    })
    .filter((g) => {
      if (!searchQuery) return true;
      return g.displayName.toLowerCase().includes(searchQuery.toLowerCase());
    });

  return (
    <>
      <div className="px-6 py-5">
        {/* Search and Add Members */}
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 dark:text-zinc-500" />
            <input
              type="text"
              placeholder="Search members"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-component rounded-md bg-zinc-50 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleAddMembersClick}
            disabled={!isOwner}
            className={cn(
              "inline-flex items-center justify-center h-9 px-4 rounded-md text-sm font-medium transition-colors flex-shrink-0",
              isOwner
                ? "bg-blue-500 text-white hover:bg-blue-700"
                : "bg-zinc-300 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400 cursor-not-allowed opacity-60",
            )}
          >
            Add members
          </button>
        </div>

        {/* Headers row */}
        <div className="mt-5 flex items-center justify-between border-b border-component pb-2">
          <button
            onClick={() => setSortBy("name")}
            title="Sort by name"
            className={cn(
              "text-xs text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors",
              sortBy === "name" && "text-zinc-900 dark:text-zinc-100",
            )}
          >
            Name
          </button>
          <button
            onClick={() => setSortBy("role")}
            title="Sort by role"
            className={cn(
              "inline-flex items-center gap-1 text-xs text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors",
              sortBy === "role" && "text-zinc-900 dark:text-zinc-100",
            )}
          >
            <ArrowDown className="w-3 h-3" />
            Role
          </button>
        </div>

        {/* Members + groups list */}
        <div>
          {filteredGroups.length === 0 && filteredAndSortedMembers.length === 0 ? (
            <div className="text-center py-8 text-sm text-zinc-500 dark:text-zinc-400">
              {searchQuery ? "No matches found" : "No members"}
            </div>
          ) : (
            <>
            {filteredGroups.map((g, i) => {
              const isLast =
                i === filteredGroups.length - 1 && filteredAndSortedMembers.length === 0;
              return (
                <div
                  key={`group-${g.gid}`}
                  className={cn(
                    "flex items-center justify-between py-3",
                    !isLast && "border-b border-zinc-200 dark:border-zinc-700",
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-8 h-8 rounded-full bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center flex-shrink-0">
                      <Users className="w-4 h-4 text-zinc-600 dark:text-zinc-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                        {g.displayName}
                      </div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                        {g.memberCount} member{g.memberCount === 1 ? "" : "s"}
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 relative">
                    {!isOwner ? (
                      <div className="inline-flex items-center gap-1 px-2 py-1 text-sm text-zinc-500 dark:text-zinc-400">
                        {getGroupPermissionDisplay(g.permission)}
                      </div>
                    ) : (
                      <>
                        <button
                          ref={(el) => {
                            buttonRefs.current[`group-${g.gid}`] = el;
                          }}
                          title="Manage group access"
                          onClick={() =>
                            setOpenDropdownId(
                              openDropdownId === `group-${g.gid}` ? null : `group-${g.gid}`,
                            )
                          }
                          className={cn(
                            "inline-flex items-center gap-1 px-2 py-1 text-sm rounded-md transition-colors",
                            openDropdownId === `group-${g.gid}`
                              ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100"
                              : "text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-zinc-100",
                          )}
                        >
                          <span>{getGroupPermissionDisplay(g.permission)}</span>
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                        {openDropdownId === `group-${g.gid}` && (
                          <AnchoredPopover
                            anchor={buttonRefs.current[`group-${g.gid}`] ?? null}
                            // Above the WorkAreaViewModal shell (z-[11000]).
                            zIndex={12000}
                            onClose={() => setOpenDropdownId(null)}
                            className="bg-modal shadow-lg border border-component rounded-md py-1 min-w-[180px]"
                          >
                            <button
                              onClick={() =>
                                handleRemoveGroup({ gid: g.gid, displayName: g.displayName })
                              }
                              disabled={removingGroupId === g.gid}
                              className={cn(
                                "w-full text-left px-3 py-2 text-sm hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                                removingGroupId === g.gid
                                  ? "text-zinc-400 dark:text-zinc-500"
                                  : "text-red-600 dark:text-red-400",
                              )}
                            >
                              <X className="w-4 h-4" />
                              <span>Remove group</span>
                            </button>
                          </AnchoredPopover>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            {filteredAndSortedMembers.map((member, index) => {
              const isCurrentUser = member.userEmail === user?.email;
              const displayName = member.userName || member.userEmail || "Unknown";
              const initials =
                member.userName?.charAt(0).toUpperCase() ||
                member.userEmail?.charAt(0).toUpperCase() ||
                "U";
              const color = avatarColor(member.userEmail || member.userId || displayName);
              const id = getMemberId(member);

              return (
                <div
                  key={id || index}
                  className={cn(
                    "flex items-center justify-between py-3",
                    index < filteredAndSortedMembers.length - 1 &&
                      "border-b border-component",
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {/* Avatar */}
                    <MemberHoverAvatar
                      name={member.userName}
                      email={member.userEmail}
                      userId={member.userId}
                      wrapperClassName="flex-shrink-0"
                      className={cn(
                        "w-8 h-8 rounded-full text-sm font-medium text-white",
                        color,
                      )}
                    />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                          {displayName}
                        </span>
                        {isCurrentUser && (
                          <span className="text-sm text-zinc-500 dark:text-zinc-400">(You)</span>
                        )}
                        {member.isWorkspaceOwner && (
                          <span className="px-1.5 py-0.5 text-[11px] font-medium text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-700 rounded">
                            Workspace owner
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                        {member.userEmail}
                      </div>
                    </div>
                  </div>

                  {/* Role display / dropdown */}
                  <div className="flex-shrink-0 relative">
                    {member.isWorkAreaOwner || !isOwner ? (
                      <div className="inline-flex items-center gap-1 px-2 py-1 text-sm text-zinc-500 dark:text-zinc-400">
                        <span>{getRoleDisplay(member.isWorkAreaOwner)}</span>
                        {!member.isWorkAreaOwner && (
                          <ChevronDown className="w-3.5 h-3.5 opacity-50" />
                        )}
                      </div>
                    ) : (
                      <>
                        <button
                          ref={(el) => {
                            buttonRefs.current[id] = el;
                          }}
                          title="Change member role"
                          onClick={() => setOpenDropdownId(openDropdownId === id ? null : id)}
                          className={cn(
                            "inline-flex items-center gap-1 px-2 py-1 text-sm rounded-md transition-colors",
                            openDropdownId === id
                              ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100"
                              : "text-zinc-500 dark:text-zinc-400 hover-component  hover:text-zinc-900 dark:hover:text-zinc-100",
                          )}
                        >
                          <span>{getRoleDisplay(false)}</span>
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                        {openDropdownId === id && (
                          <AnchoredPopover
                            anchor={buttonRefs.current[id] ?? null}
                            // Above the WorkAreaViewModal shell (z-[11000]).
                            zIndex={12000}
                            onClose={() => setOpenDropdownId(null)}
                            className="bg-modal shadow-lg border border-component rounded-md py-1 min-w-[180px]"
                          >
                            <button
                              onClick={() =>
                                handleRemoveMember({
                                  userId: member.userId,
                                  userEmail: member.userEmail,
                                  userName: member.userName,
                                  isWorkAreaOwner: member.isWorkAreaOwner,
                                })
                              }
                              disabled={removingMemberId === id}
                              className={cn(
                                "w-full text-left px-3 py-2 text-sm hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                                removingMemberId === id
                                  ? "text-zinc-400 dark:text-zinc-500"
                                  : "text-red-600 dark:text-red-400",
                              )}
                            >
                              <X className="w-4 h-4" />
                              <span>Remove member</span>
                            </button>
                          </AnchoredPopover>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            </>
          )}
        </div>
      </div>

      <WorkAreaAddMembersModal
        workArea={workArea}
        isOpen={showAddMembersModal}
        onClose={() => setShowAddMembersModal(false)}
        onMembersAdded={handleMembersAdded}
      />
    </>
  );
}
